import { useCallback, useEffect, useMemo } from 'react';
import throttle from '@core/lodash/throttle';
import uniqBy from '@core/lodash/uniqBy';
import { logError } from '@core/logging';
import { useEmbedFeedStoreApi } from '@core/widgets/embedFeed/store';
import { useStoreApi } from '@core/widgets/store';
import { PlaySourceEnum } from '@embed/constants';
import { useTrackThumbnailImpression } from './useTrackThumbnailImpression';
export const THROTTLE_THUMBNAIL_IMPRESSION_TIME = 1000;
/**
 * Hook provides a callback for thumbnail impression tracking.
 * The callback is throttled, it pulls new videos from the queue,
 * sends a tracking event, and processes the videos in the store.
 */
export function useTrackThumbnailImpressionsEmbedFeed() {
    const trackThumbnailImpression = useTrackThumbnailImpression();
    const storeApi = useStoreApi();
    const embedStoreApi = useEmbedFeedStoreApi();
    const trackEvent = useCallback(() => {
        const getVideosFromQueue = (queue, success) => {
            const videos = queue.filter((video) => !success[video.encoded_id]);
            return uniqBy(videos, (video) => video.encoded_id);
        };
        const store = storeApi.getState();
        const embedStore = embedStoreApi.getState();
        const videos = getVideosFromQueue(embedStore.thumbnailImpressionVideoReportQueue, embedStore.thumbnailImpressionIdsReportSuccess);
        if (store.appContext) {
            trackThumbnailImpression(videos, PlaySourceEnum.AUTOPLAY, store.appContext);
        }
        else {
            logError(new Error('AppContext is not defined before triggering thumbnail impression event'));
        }
        embedStore.actions.processThumbnailImpressionQueue(videos);
    }, [storeApi, embedStoreApi, trackThumbnailImpression]);
    const trackEventThrottled = useMemo(() => throttle(trackEvent, THROTTLE_THUMBNAIL_IMPRESSION_TIME, {
        leading: false,
    }), [trackEvent]);
    // Cancel throttled function on unmount
    useEffect(() => trackEventThrottled.cancel, [trackEventThrottled]);
    return trackEventThrottled;
}
